import { Injectable } from "@angular/core";
import { doc, DocumentData, Firestore, getDoc, onSnapshot } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { CatchErrorService } from "@core/catch-error.service";
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from "rxjs/operators";
@Injectable({
  providedIn: "root",
})
export class JackpotTriggersService {
  public selectedJackpotTrigger: Data;
  private xpAnimationStopSubject = new BehaviorSubject<boolean | null>(null);
  public xpAnimationStop$ = this.xpAnimationStopSubject.asObservable();

  private triggerActivationStatusSubject = new BehaviorSubject<boolean | null>(null);
  public triggerActivationStatus$ = this.triggerActivationStatusSubject.asObservable();

  constructor(private firestore: Firestore, private catchService: CatchErrorService) { }

  setXpAnimationStop(status: boolean): void {
    this.xpAnimationStopSubject.next(status);
  }

  setTriggerActivationStatus(status: boolean): void {
    this.triggerActivationStatusSubject.next(status);
  }

  getActivateTriggerPopupData(): Observable<any> {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          return of(null); // Handle offline scenario gracefully
        }
        const triggerDocRef = doc(this.firestore, `Global_lookup/onboarding/slider_show/jackpot_triggers`);
        return new Observable((subscriber) => {
          const unsubscribe = onSnapshot(
            triggerDocRef,
            (snapshot) => subscriber.next(snapshot),
            (error) => subscriber.error(error)
          );
          return () => unsubscribe();
        }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1),
      map((data: any) => data?.data()) // Extract the document data
    );
  }

  getTranslatedData(languageCode: string): Observable<any> {
    // Validate languageCode and provide default fallback to prevent invalid Firestore paths
    const validLanguageCode = languageCode && typeof languageCode === "string" && languageCode.trim() !== "" 
      ? languageCode 
      : "en";

    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          return of(null); // Handle offline scenario gracefully
        }
        const translateDocRef = doc(this.firestore, `/translates/${validLanguageCode}`);
        return new Observable((subscriber) => {
          const unsubscribe = onSnapshot(
            translateDocRef,
            (snapshot) => subscriber.next(snapshot),
            (error) => {
              console.error("Error fetching translated data", error);
              subscriber.error(error);
            }
          );
          return () => unsubscribe();
        }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1),
      map((data: any) => {
        // Handle null data from offline scenario or failed snapshot
        if (!data) {
          return null;
        }
        // Safely extract document data
        return data?.data?.() ?? null;
      })
    );
  }
  public storeJackpotData(data: Data) {
    this.selectedJackpotTrigger = data;
  }

  public shareJackpotData() {
    return this.selectedJackpotTrigger;
  }
  getUserTriggerData(userId: string, triggerId: string): Promise<DocumentData | undefined> {
    const path = `users/${userId}/jackpot_trigger/${triggerId}`;
    const triggerDocRef = doc(this.firestore, path);

    return getDoc(triggerDocRef)
      .then((docSnapshot) => docSnapshot.data())
      .catch((error) => {
        console.error("Error fetching user trigger data:", error);
        throw error;
      });
  }

}
